(() => {
  'use strict';

  const EASINGS = {
    luxury: 'cubic-bezier(.16,1,.3,1)',
    smooth: 'cubic-bezier(.22,.61,.36,1)',
    soft: 'cubic-bezier(.25,.46,.45,.94)',
    snappy: 'cubic-bezier(.2,.8,.2,1)',
    linear: 'linear'
  };

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getTargets = (root) => {
    const nested = [...root.querySelectorAll('.zavo-reveal,[data-zavo-motion-item]')];
    return nested.length ? nested : [root];
  };

  const reveal = (root, visible = true) => {
    root.classList.toggle('is-zavo-motion-visible', visible);
    getTargets(root).forEach((el) => el.classList.toggle('is-zavo-motion-visible', visible));
  };

  const configure = (root) => {
    if (root.dataset.zavoMotionReady === 'true') return;
    root.dataset.zavoMotionReady = 'true';

    const globalEnabled = document.body?.dataset.zavoMotionGlobal !== 'false';
    const enabled = globalEnabled && root.dataset.zavoMotionEnabled !== 'false';
    const type = root.dataset.zavoMotion || root.dataset.zavoMotionType || 'fade-up';
    const duration = Number(root.dataset.zavoMotionDuration || 800);
    const delay = Number(root.dataset.zavoMotionDelay || 0);
    const stagger = Number(root.dataset.zavoMotionStagger || 90);
    const distance = Number(root.dataset.zavoMotionDistance || 28);
    const easing = EASINGS[root.dataset.zavoMotionEasing] || EASINGS.luxury;
    const once = root.dataset.zavoMotionOnce !== 'false';
    const threshold = Math.min(.8, Math.max(0, Number(root.dataset.zavoMotionThreshold || 12) / 100));

    root.dataset.zavoMotionType = enabled ? type : 'none';
    root.dataset.zavoMotionActive = enabled ? 'true' : 'false';
    root.style.setProperty('--zavo-motion-duration', `${duration}ms`);
    root.style.setProperty('--zavo-motion-delay', `${delay}ms`);
    root.style.setProperty('--zavo-motion-stagger', `${stagger}ms`);
    root.style.setProperty('--zavo-motion-distance', `${distance}px`);
    root.style.setProperty('--zavo-motion-ease', easing);

    const hoverStrength = Number(root.dataset.zavoHoverStrength || 5);
    root.style.setProperty('--zavo-hover-strength', hoverStrength);

    const targets = getTargets(root);
    targets.forEach((el, index) => {
      el.classList.add('zavo-motion-target');
      el.style.setProperty('--zavo-motion-index', index);
    });

    if (!enabled || type === 'none' || reduceMotion() || !('IntersectionObserver' in window)) {
      reveal(root, true);
      return;
    }

    const observer = new IntersectionObserver((entries, io) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          reveal(root, true);
          if (once) io.unobserve(root);
        } else if (!once) {
          reveal(root, false);
        }
      });
    }, { threshold, rootMargin: '0px 0px -3% 0px' });

    observer.observe(root);
  };

  const initParallax = (scope = document) => {
    if (reduceMotion() || document.body?.dataset.zavoParallaxGlobal === 'false') return;
    const roots = [...scope.querySelectorAll('[data-zavo-parallax]:not([data-zavo-parallax-ready])')];
    if (!roots.length) return;
    roots.forEach((root) => root.dataset.zavoParallaxReady = 'true');

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight || 1;
      roots.forEach((root) => {
        if (root.dataset.zavoParallax === 'false') return;
        const rect = root.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        const strength = Number(root.dataset.zavoParallaxStrength || 18);
        const center = rect.top + rect.height / 2;
        const progress = (center - vh / 2) / (vh + rect.height);
        root.style.setProperty('--zavo-parallax-y', `${progress * strength * -1}px`);
      });
    };
    const request = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
  };

  const init = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-zavo-motion]:not([data-zavo-motion-ready])')) configure(scope);
    scope.querySelectorAll?.('[data-zavo-motion]:not([data-zavo-motion-ready])').forEach(configure);
    initParallax(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => init());
  document.addEventListener('zavo:recent-rendered', (event) => { if (event.detail?.root) init(event.detail.root); });
})();
